/* eslint-disable parog-web/no-glint-expect-error-directive */
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import FuiDropdownItem from 'parog-web/components/fui-dropdown/item';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

export type Value =
  | Record<string, unknown>
  | Array<string>
  | string
  | undefined;
interface Settings {
  onChange?: (value: Value) => unknown;
  forceSelection?: boolean;
  onSearch?: (value: string) => unknown;
  message?: {
    noResults: string;
  };
  onAdd?: (value: string) => unknown;
  onRemove?: (value: string) => unknown;
  fullTextSearch?: boolean;
}
type Guid = ReturnType<typeof guidFor>;

interface Signature {
  Element: HTMLDivElement;
  Args: {
    settings?: Settings;
    selected: Value;
    id?: string;
  };
}

export default class FuiDropdownComponent extends Component<Signature> {
  get selected(): Guid | undefined {
    return this.valueMapper(this.args.selected);
  }

  get settings(): Settings {
    const newOnChangeSetting: { onChange?: (guid: Guid) => unknown } = {};
    if (this.#onChangeFromGuid)
      newOnChangeSetting.onChange = this.#onChangeFromGuid;

    // replace the `onChange` provided in the @settings argument with the same method,
    // but this new one receives a Guid instead
    return Object.assign({}, this.args.settings, newOnChangeSetting);
  }

  #valuesMap = new Map<Value, Guid | undefined>();

  get #initialOnChange(): NonNullable<
    Signature['Args']['settings']
  >['onChange'] {
    if (!this.args.settings) return undefined;

    return this.args.settings.onChange;
  }

  get #onChangeFromGuid(): ((guid: Guid) => unknown) | undefined {
    if (!this.#initialOnChange) return undefined;

    const initialOnChange = this.#initialOnChange;

    return (guid: Guid) => {
      const newValue = this.#getValueFromGuid(guid);

      if (!newValue) {
        console.log(`No value found for guid ${guid}`);
        return;
      }

      initialOnChange(newValue);
    };
  }

  registerValue = (value: Value): void => {
    this.#valuesMap.set(value, this.valueMapper(value));
  };

  triggerChange = modifier((e: HTMLElement): void => {
    // We need to call this property so that ember-modifier can execute the function when it is updated
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
    this.args.selected;

    jQuery(e).data().moduleDropdown.trigger.change();
  });

  valueMapper(value: Value): Guid | string | undefined {
    if (value instanceof Array) {
      return value
        .map(i => {
          return typeof i === 'string' ? i : guidFor(i);
        })
        .join(',');
    }

    return typeof value === 'string' ? value : guidFor(value);
  }

  #getValueFromGuid(guid: Guid): Value | undefined {
    const guidAsArr = guid.split(',');
    const tuple = [...this.#valuesMap].find(t =>
      guidAsArr.find(guid => guid === t[1])
    );
    if (!tuple) return undefined;

    return tuple[0];
  }

  <template>
    <div
      class='ui dropdown'
      {{! @glint-expect-error }}
      {{fuiDropdown this.settings}}
      {{this.triggerChange}}
      ...attributes
    >
      <input id={{@id}} type='hidden' value={{this.selected}} />

      {{! @glint-expect-error }}
      <div class='default text'>{{yield to='defaultText'}}</div>

      <i class='dropdown icon'></i>

      {{!-- prettier-ignore --}}
      <div class="menu">

              {{! @glint-expect-error }}
    {{yield (component FuiDropdownItem onInit=this.registerValue valueMapper=this.valueMapper) to="menu"}}
  </div>
    </div>
  </template>
}
